/**
 * Content policy features
 * @module services/content
 */
import { stringify as qsStringifyQuery } from 'qs'
import { fromJS, Map, Set, List } from 'immutable'
import get from 'lodash/get'
import isUndefined from 'lodash/isUndefined'
import omitBy from 'lodash/omitBy'
import toNumber from 'lodash/toNumber'
import strictUriEncode from 'strict-uri-encode'
import request from '../request'
import { createAuthorization } from '../auth'
import { getLocationQuery, getLocationPathname } from '../location'
import matchPath from '../../routes/match-path'
import { getApplicationDataByKey } from '../data'

/**
 * The application key for content detail
 */
export const CONTENT_POLICY_DETAIL_APPLICATION_KEY = 'detail'
export const CONTENT_POLICY_STORE_KEY = 'contentPolicy'
export const CONTENT_POLICY_ID_REGEX = /^[0-9]+$/

/**
 * The application key for content policy create
 */
export const CONTENT_POLICY_CREATE_APPLICATION_KEY = 'create'

/**
 * Get a list of content polices from the backend
 * @param {Object} options The options
 * @param {String} options.server The server to call
 * @param {Map} options.auth The auth information
 * @param {Number} options.policyId The content policy id
 * @param {Number} options.offset The list offset
 * @param {Number} options.limit The list limit
 * @param {String} options.sort The sort type
 * @param {String} options.sortDirection The sort direction
 */
export async function getContentPolicyList (options = {}) {
  const { auth, server, policyId, offset, limit, sort, sortDirection } = options
  const params = omitBy(
    {
      policyId: List.isList(policyId) || Set.isSet(policyId) ? policyId.toJS() : policyId,
      offset,
      limit,
      sort,
      sortDirection,
    },
    isUndefined,
  )
  const res = await request
    .get(`${server}/v1/content-policy/list`)
    .set('accept', 'application/json')
    .set('authorization', createAuthorization(auth))
    .query(qsStringifyQuery(params, { arrayFormat: 'indices', encodeValuesOnly: true }))
  return fromJS(get(res, 'body', {}))
}

export const getCurrentContentPolicyDetail = (props) => {
  const { data, location, storeKey, applicationKey } = props
  const id = getPolicyIdFromLocation(location)
  const detail =
    getApplicationDataByKey({
      data,
      storeKey,
      applicationKey,
    }) || Map()
  return detail.get('policyId') === id ? detail : Map()
}

/**
 * Create a content policy
 * @param {Object} options The options
 * @param {String} options.server The server to call
 * @param {Map} options.auth The auth information
 * @param {Map} options.data The content policy data
 */
export async function createContentPolicy (options = {}) {
  const { auth, server, data } = options
  if (!Map.isMap(data)) {
    throw new Error('The data option is required to be a Map')
  }
  const res = await request
    .post(`${server}/v1/content-policy`)
    .send(data.toJS())
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Update a content policy
 * @param {Object} options The options
 * @param {String} options.server The server to call
 * @param {Map} options.auth The auth information
 * @param {Map} options.data The content policy data
 */
export async function updateContentPolicy (options = {}) {
  const { auth, server, data } = options
  if (!Map.isMap(data)) {
    throw new Error('The data option is required to be a Map')
  }
  if (!data.get('policyId')) {
    throw new Error('The policyId is a required propery on data')
  }
  const res = await request
    .put(`${server}/v1/content-policy/${strictUriEncode(data.get('policyId'))}`)
    .send(data.toJS())
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}

/**
 * Given the location state get the policy id
 * @param {Map} location The location state
 * @returns {String|undefined} The content policy id if found in the location
 */
export function getPolicyIdFromLocation (location = Map()) {
  if (!Map.isMap(location)) {
    throw new Error('The location argument is required to be a Map')
  }
  const match = matchPath({
    pathname: getLocationPathname(location),
    path: '/content-policy/:policyId',
  })
  const policyId = get(match, 'params.policyId')
  if (!policyId) {
    return policyId
  }
  return CONTENT_POLICY_ID_REGEX.test(policyId) ? toNumber(policyId) : policyId
}

/**
 * Return true when in content detail page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname starts '/content'
 */
export function isContentPolicyDetailPage (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const id = getPolicyIdFromLocation(location)
  return CONTENT_POLICY_ID_REGEX.test(id)
}

/**
 * Return true when in content policy list page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname starts '/content'
 */
export function isContentPolicyListPage (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return !!matchPath({
    pathname: getLocationPathname(location),
    path: '/content-policy',
  })
}

/**
 * Given state create the content policy list options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createContentPolicyListViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  const query = getLocationQuery(location)
  return Map({
    offset: query.get('offset') || 0,
    limit: query.get('limit') || 50,
    policyId: query.get('policyId'),
    sort: 'policy-id',
    sortDirection: 'desc',
  })
}

/**
 * Given state create the content policy detail options
 * @param {Object} state The parameters
 * @param {Map} state.location The location state
 */
export function createContentPolicyDetailViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return Map({
    policyId: getPolicyIdFromLocation(location),
    include: Set(),
  })
}

/**
 * Given state create the content policy create options
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 */
export function createContentPolicyCreateViewOptions (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return Map({ create: true })
}

/**
 * Given state create the content policy path create path options
 * @param {Object} state Redux state
 * @param {Map} state.content The content state
 */
export function createContentPolicyPathCreateViewOptions (state = {}) {
  const { contentPolicy } = state
  if (!Map.isMap(contentPolicy)) {
    throw new Error('The content policy property is required to be a Map on the state parameter')
  }
  return Map({
    policyId: contentPolicy.get('policyId'),
    name: contentPolicy.get('name'),
  })
}

/**
 * Returns true when shareAllowed is true or undefined
 * @param {Map} [contentPolicy] A content policy Map
 * @return {Boolean} True when content policy exist and shareAllowed is true or undefined
 */
export function getContentPolicyIsSharable (contentPolicy) {
  if (!contentPolicy) {
    return true
  }
  return (
    contentPolicy.get('shareAllowed') === true || isUndefined(contentPolicy.get('shareAllowed'))
  )
}

/**
 * Return true when in content detail page
 * @param {Object} state Redux state
 * @param {Map} state.location The location state
 * @return {Boolean} True when the pathname is '/content/create'
 */
export function isContentPolicyCreatePage (state = {}) {
  const { location } = state
  if (!Map.isMap(location)) {
    throw new Error('The location property is required to be a Map on the state parameter')
  }
  return getLocationPathname(location) === '/content-policy/create'
}

/**
 * Remove content
 * @param {Object} options The options
 * @param {Map} options.auth The auth state
 * @param {String} options.server The server
 * @param {Map} options.data The content data
 */
export async function removeContentPolicy (options = {}) {
  const { auth, server, policyId } = options
  if (!policyId) {
    throw new Error('The policyId option is required')
  }
  const res = await request
    .delete(`${server}/v1/content-policy/${policyId}`)
    .set('authorization', createAuthorization(auth))
    .set('accept', 'application/json')
  return fromJS(get(res, 'body', {}))
}
